import React, { useState, useEffect } from 'react';
import { COLORS } from '../App';

const API_BASE = window.location.port === '3000' ? 'http://localhost:4100/api' : window.location.pathname.startsWith('/cmbs') ? '/cmbs/api' : '/api';

function apiFetch(path, opts = {}) {
  const token = localStorage.getItem('cmbs_token');
  const headers = { ...opts.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (opts.body && !(opts.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
    opts.body = JSON.stringify(opts.body);
  }
  return fetch(`${API_BASE}${path}`, { ...opts, headers }).then((r) => r.json());
}

const SECTION_LABELS = {
  executive_summary: 'Executive Summary',
  property_description: 'Property Description',
  collateral_description: 'Collateral Description',
  borrower_guarantor: 'Borrower and Guarantor',
  market_overview: 'Market Overview',
  underwriting_narrative: 'Underwriting Summary',
  sponsor_overview: 'Sponsor Overview',
  conditions_covenants: 'Conditions and Covenants',
  acceptance_terms: 'Acceptance Terms',
  opening: 'Opening',
  proposed_terms_narrative: 'Proposed Terms',
  conditions: 'Conditions',
  closing: 'Closing',
  recommendation: 'Recommendation',
  sensitivity_narrative: 'Sensitivity Analysis',
  environmental_summary: 'Environmental Summary',
};

export default function DocumentEditor({ docId, onClose, onSave }) {
  const [content, setContent] = useState(null);
  const [html, setHtml] = useState('');
  const [docType, setDocType] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(null);

  useEffect(() => { loadDoc(); }, [docId]);

  const loadDoc = async () => {
    const res = await apiFetch(`/documents/preview/${docId}`);
    if (res.success) {
      setContent(res.data.content);
      setHtml(res.data.html);
      setDocType(res.data.doc_type);
    }
    setLoading(false);
  };

  const handleSectionChange = (key, value) => {
    setContent({ ...content, [key]: value });
  };

  const handleSave = async () => {
    setSaving(true);
    await apiFetch(`/documents/content/${docId}`, { method: 'PUT', body: { content } });
    setSaving(false);
    if (onSave) onSave();
    await loadDoc();
  };

  const handleRegenerate = async (key) => {
    setRegenerating(key);
    const res = await apiFetch(`/documents/regenerate-section/${docId}`, {
      method: 'POST', body: { section_key: key },
    });
    if (res.success) {
      setContent({ ...content, [key]: res.data.content });
      await loadDoc();
    }
    setRegenerating(null);
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading editor...</div>;

  const editableSections = [];
  if (content) {
    for (const [key, val] of Object.entries(content)) {
      if (typeof val === 'string' && val.length > 20 && SECTION_LABELS[key]) {
        editableSections.push({ key, label: SECTION_LABELS[key], value: val });
      }
    }
  }

  const hasStrengths = content?.strengths && Array.isArray(content.strengths);
  const hasConsiderations = content?.considerations && Array.isArray(content.considerations);
  const isOnePager = docType === 'one_pager';

  const inputStyle = { width: '100%', padding: '6px 10px', border: `1px solid ${COLORS.border}`, borderRadius: 4, fontSize: 13 };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: '#fff', zIndex: 1000, display: 'flex', flexDirection: 'column' }}>
      <div style={{ background: COLORS.navy, padding: '10px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
        <h3 style={{ fontFamily: 'Georgia, serif', color: COLORS.gold, fontSize: 16, margin: 0 }}>Edit Document Content</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={handleSave} disabled={saving}
            style={{ padding: '6px 18px', background: COLORS.gold, color: COLORS.navy, border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
          <button onClick={onClose}
            style={{ padding: '6px 18px', background: 'transparent', border: `1px solid ${COLORS.gold}`, color: COLORS.gold, borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>
            Close
          </button>
        </div>
      </div>

      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        <div style={{ width: '50%', overflow: 'auto', background: '#E0E0E0', padding: 20 }}>
          <div style={{ background: '#fff', maxWidth: 680, margin: '0 auto', padding: 40, boxShadow: '0 2px 12px rgba(0,0,0,0.15)', minHeight: 900 }}>
            <div dangerouslySetInnerHTML={{ __html: html }} />
          </div>
        </div>

        <div style={{ width: '50%', overflow: 'auto', padding: 20, background: COLORS.bg }}>
          <p style={{ fontSize: 12, color: COLORS.textLight, marginBottom: 16 }}>Edit generated content below. Changes update the preview after saving.</p>

          {/* One-pager specific editor */}
          {isOnePager && content && (
            <>
              {/* Key Strengths */}
              <div style={{ background: '#fff', borderRadius: 8, border: `1px solid ${COLORS.border}`, padding: 16, marginBottom: 16 }}>
                <label style={{ fontFamily: 'Georgia, serif', fontSize: 14, color: COLORS.navy, fontWeight: 600, display: 'block', marginBottom: 8 }}>Key Strengths</label>
                <p style={{ fontSize: 11, color: COLORS.textLight, marginBottom: 8 }}>3–4 bullets, max 10 words each.</p>
                {(content.key_strengths || []).map((s, i) => (
                  <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                    <span style={{ background: COLORS.gold, color: COLORS.navy, borderRadius: 2, padding: '2px 7px', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{i + 1}</span>
                    <input value={s} onChange={(e) => {
                      const updated = [...content.key_strengths];
                      updated[i] = e.target.value;
                      setContent({ ...content, key_strengths: updated });
                    }} style={inputStyle} />
                    <button onClick={() => {
                      const updated = content.key_strengths.filter((_, idx) => idx !== i);
                      setContent({ ...content, key_strengths: updated });
                    }} style={{ padding: '4px 10px', background: '#FDE8E8', color: COLORS.danger, border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, flexShrink: 0 }}>✕</button>
                  </div>
                ))}
                {(content.key_strengths || []).length < 4 && (
                  <button onClick={() => setContent({ ...content, key_strengths: [...(content.key_strengths || []), ''] })}
                    style={{ marginTop: 4, padding: '5px 12px', background: '#F0F2F5', border: `1px solid ${COLORS.border}`, borderRadius: 4, fontSize: 11, cursor: 'pointer', color: COLORS.navy }}>
                    + Add Strength
                  </button>
                )}
              </div>

              {/* Spread Indication */}
              <div style={{ background: '#fff', borderRadius: 8, border: `1px solid ${COLORS.border}`, padding: 16, marginBottom: 16 }}>
                <label style={{ fontFamily: 'Georgia, serif', fontSize: 14, color: COLORS.navy, fontWeight: 600, display: 'block', marginBottom: 8 }}>Spread Indication</label>
                <input value={content.loan_summary?.spread_indication || ''} onChange={(e) =>
                  setContent({ ...content, loan_summary: { ...content.loan_summary, spread_indication: e.target.value } })}
                  placeholder="e.g. T+180 bps" style={inputStyle} />
              </div>

              {/* Sponsor Name */}
              <div style={{ background: '#fff', borderRadius: 8, border: `1px solid ${COLORS.border}`, padding: 16, marginBottom: 16 }}>
                <label style={{ fontFamily: 'Georgia, serif', fontSize: 14, color: COLORS.navy, fontWeight: 600, display: 'block', marginBottom: 8 }}>Sponsor Name</label>
                <input value={content.sponsor_and_metrics?.sponsor_name || ''} onChange={(e) =>
                  setContent({ ...content, sponsor_and_metrics: { ...content.sponsor_and_metrics, sponsor_name: e.target.value } })}
                  style={inputStyle} />
              </div>

              {/* Lender Contact */}
              <div style={{ background: '#fff', borderRadius: 8, border: `1px solid ${COLORS.border}`, padding: 16, marginBottom: 16 }}>
                <label style={{ fontFamily: 'Georgia, serif', fontSize: 14, color: COLORS.navy, fontWeight: 600, display: 'block', marginBottom: 8 }}>Lender Contact (Footer)</label>
                {[
                  { field: 'originator_name', label: 'Originator Name' },
                  { field: 'desk', label: 'Desk' },
                  { field: 'phone_placeholder', label: 'Phone' },
                  { field: 'email_placeholder', label: 'Email' },
                ].map(({ field, label }) => (
                  <div key={field} style={{ marginBottom: 8 }}>
                    <label style={{ fontSize: 11, color: COLORS.textLight, display: 'block', marginBottom: 2 }}>{label}</label>
                    <input value={content.lender_contact?.[field] || ''} onChange={(e) =>
                      setContent({ ...content, lender_contact: { ...content.lender_contact, [field]: e.target.value } })}
                      style={inputStyle} />
                  </div>
                ))}
              </div>

              {/* Read-only info note */}
              <div style={{ background: '#F8F9FA', borderRadius: 8, border: `1px solid ${COLORS.border}`, padding: 12, marginBottom: 16 }}>
                <p style={{ fontSize: 11, color: COLORS.textLight, margin: 0 }}>
                  Other fields (loan amount, metrics, property details) are auto-populated from the deal record and underwriting results.
                </p>
              </div>
            </>
          )}

          {/* Standard text section editors (non-one-pager) */}
          {!isOnePager && editableSections.map((sec) => (
            <div key={sec.key} style={{ background: '#fff', borderRadius: 8, border: `1px solid ${COLORS.border}`, padding: 16, marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <label style={{ fontFamily: 'Georgia, serif', fontSize: 14, color: COLORS.navy, fontWeight: 600 }}>{sec.label}</label>
                <button onClick={() => handleRegenerate(sec.key)} disabled={regenerating === sec.key}
                  style={{ padding: '4px 12px', background: '#F0F2F5', border: `1px solid ${COLORS.border}`, borderRadius: 4, fontSize: 11, cursor: 'pointer', color: COLORS.navy }}>
                  {regenerating === sec.key ? 'Regenerating...' : 'Regenerate'}
                </button>
              </div>
              <textarea
                value={content[sec.key] || ''}
                onChange={(e) => handleSectionChange(sec.key, e.target.value)}
                rows={Math.max(4, Math.ceil(sec.value.length / 80))}
                style={{ width: '100%', padding: '10px 12px', border: `1px solid ${COLORS.border}`, borderRadius: 4, fontSize: 13, resize: 'vertical', lineHeight: 1.6, fontFamily: 'Calibri, Arial, sans-serif' }}
              />
            </div>
          ))}

          {/* Strengths editor */}
          {!isOnePager && hasStrengths && (
            <div style={{ background: '#fff', borderRadius: 8, border: `1px solid ${COLORS.border}`, padding: 16, marginBottom: 16 }}>
              <label style={{ fontFamily: 'Georgia, serif', fontSize: 14, color: COLORS.navy, fontWeight: 600, display: 'block', marginBottom: 8 }}>Strengths</label>
              {content.strengths.map((s, i) => (
                <div key={i} style={{ borderLeft: `3px solid ${COLORS.gold}`, paddingLeft: 12, marginBottom: 12 }}>
                  <input value={s.title} onChange={(e) => {
                    const updated = [...content.strengths];
                    updated[i] = { ...updated[i], title: e.target.value };
                    setContent({ ...content, strengths: updated });
                  }} style={{ width: '100%', padding: '6px 10px', border: `1px solid ${COLORS.border}`, borderRadius: 4, fontSize: 13, fontWeight: 600, marginBottom: 4 }} />
                  <textarea value={s.description} onChange={(e) => {
                    const updated = [...content.strengths];
                    updated[i] = { ...updated[i], description: e.target.value };
                    setContent({ ...content, strengths: updated });
                  }} rows={3} style={{ width: '100%', padding: '6px 10px', border: `1px solid ${COLORS.border}`, borderRadius: 4, fontSize: 12, resize: 'vertical' }} />
                </div>
              ))}
            </div>
          )}

          {/* Considerations editor */}
          {!isOnePager && hasConsiderations && (
            <div style={{ background: '#fff', borderRadius: 8, border: `1px solid ${COLORS.border}`, padding: 16, marginBottom: 16 }}>
              <label style={{ fontFamily: 'Georgia, serif', fontSize: 14, color: COLORS.navy, fontWeight: 600, display: 'block', marginBottom: 8 }}>Considerations and Mitigants</label>
              {content.considerations.map((c, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
                  <textarea value={c.consideration} onChange={(e) => {
                    const updated = [...content.considerations];
                    updated[i] = { ...updated[i], consideration: e.target.value };
                    setContent({ ...content, considerations: updated });
                  }} rows={3} placeholder="Consideration" style={{ padding: '6px 10px', border: `1px solid ${COLORS.border}`, borderRadius: 4, fontSize: 12, resize: 'vertical' }} />
                  <textarea value={c.mitigant} onChange={(e) => {
                    const updated = [...content.considerations];
                    updated[i] = { ...updated[i], mitigant: e.target.value };
                    setContent({ ...content, considerations: updated });
                  }} rows={3} placeholder="Mitigant" style={{ padding: '6px 10px', border: `1px solid ${COLORS.border}`, borderRadius: 4, fontSize: 12, resize: 'vertical' }} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
